import { isValidDate } from '@uturn/utilities/browser';
import { DateFieldKey, FIELD_KEYS } from './field-keys';
import { Baselines, Row } from './multi-update-types';

// If the ISO string has no timezone, add "Z" (UTC) to avoid local drift
export const ensureIsoWithTZ = (s: string): string =>
	/([zZ]|[+-]\d{2}:?\d{2})$/.test(s) ? s : `${s}Z`;

// Safe Date construction (returns undefined if missing)
export const toDateSafe = (s?: string) =>
	s ? new Date(ensureIsoWithTZ(s)) : undefined;

// normalize unknown → string[]
export const asArray = (v: unknown): string[] => {
	if (!v) {
		return [];
	}
	if (Array.isArray(v)) {
		return v.filter((x): x is string => typeof x === 'string' && x);
	}

	if (typeof v === 'object' && Array.isArray((v as any).values)) {
		return ((v as any).values as unknown[]).filter(
			(x): x is string => typeof x === 'string' && x,
		);
	}
	if (typeof v === 'string') {
		return [v];
	}
	return [];
};

// When baselines are “mixed”, choose the latest date
export const maxIsoFromAny = (v: unknown): string | undefined => {
	const arr = asArray(v);
	if (!arr.length) {
		return undefined;
	}
	return arr.reduce((a, b) =>
		toDateSafe(b)!.getTime() >= toDateSafe(a)!.getTime() ? b : a,
	);
};

// When baselines are “mixed”, choose the earliest date. Used when publishing:
// the API stamps `publishFrom = now` on every shipment, so the earliest date is
// the binding constraint for the "must be in the future" check — the latest
// would hide a past value on one shipment and let the bulk publish through.
export const minIsoFromAny = (v: unknown): string | undefined => {
	const arr = asArray(v);
	if (!arr.length) {
		return undefined;
	}
	return arr.reduce((a, b) =>
		toDateSafe(b)!.getTime() <= toDateSafe(a)!.getTime() ? b : a,
	);
};

const isNonEmptyString = (v: unknown): v is string =>
	typeof v === 'string' && v.trim().length > 0;

// Try to read the user-entered date (New value) for a specific key
export function getUserDate(
	rows: Row[],
	key: DateFieldKey,
): string | undefined {
	const row = rows.find((r) => r?.key === key);
	const v = row?.value;
	if (isValidDate(v)) {
		return v.toISOString();
	}
	if (isNonEmptyString(v)) {
		return ensureIsoWithTZ(v);
	}
	return undefined;
}

// Get the “latest” baseline date from server for that key
export function getBaselineDate(
	baselines: Baselines,
	key: DateFieldKey,
): string | undefined {
	return maxIsoFromAny(baselines[key]);
}

// Prefer user date; fall back to baseline
export function getEffectiveDates(
	rows: Row[],
	baselines: Baselines,
	publishing = false,
): Partial<Record<DateFieldKey, string>> {
	const pickupUntilUserDate = getUserDate(rows, FIELD_KEYS.PICKUP_DATE_UNTIL);

	// When publishing a mixed group with no edited dates, the API stamps
	// `publishFrom = now` and rejects any shipment whose publish-until (or
	// pick-up-until) is already in the past. Validate the earliest stored value
	// (worst case) instead of the latest so those "must be in the future" checks
	// catch the whole group. Both pick-up dates use the earliest baseline so the
	// paired "from <= until" check stays consistent (each shipment already
	// satisfies from <= until, so min(from) <= min(until) still holds).
	const baselineFor = (key: DateFieldKey): string | undefined =>
		publishing ? minIsoFromAny(baselines[key]) : getBaselineDate(baselines, key);

	return {
		[FIELD_KEYS.PICKUP_DATE_FROM]:
			getUserDate(rows, FIELD_KEYS.PICKUP_DATE_FROM) ??
			baselineFor(FIELD_KEYS.PICKUP_DATE_FROM),

		[FIELD_KEYS.PICKUP_DATE_UNTIL]:
			pickupUntilUserDate ?? baselineFor(FIELD_KEYS.PICKUP_DATE_UNTIL),

		[FIELD_KEYS.DROP_DATE_FROM]:
			getUserDate(rows, FIELD_KEYS.DROP_DATE_FROM) ??
			getBaselineDate(baselines, FIELD_KEYS.DROP_DATE_FROM),

		[FIELD_KEYS.DROP_DATE_UNTIL]:
			getUserDate(rows, FIELD_KEYS.DROP_DATE_UNTIL) ??
			getBaselineDate(baselines, FIELD_KEYS.DROP_DATE_UNTIL),

		// On submit, editing pick-up-until overwrites publish-until with the same
		// value (see mapRowsToMultiUpdateRequestPayload). Mirror that sync here so
		// we validate what will actually be saved — otherwise we'd validate the
		// stale publish-until baseline and wrongly report "Must be in the future"
		// on the pick-up-until row even when the new pick-up-until is valid.
		[FIELD_KEYS.PUBLISH_UNTIL]:
			pickupUntilUserDate ??
			getUserDate(rows, FIELD_KEYS.PUBLISH_UNTIL) ??
			baselineFor(FIELD_KEYS.PUBLISH_UNTIL),
	};
}

// Build validator input (PICKUP / DROP) from effective dates
export function buildLocations(eff: Partial<Record<DateFieldKey, string>>) {
	return [
		{
			locationActionType: 'PICKUP',
			dateFrom: eff[FIELD_KEYS.PICKUP_DATE_FROM],
			dateUntil: eff[FIELD_KEYS.PICKUP_DATE_UNTIL],
		},
		{
			locationActionType: 'DROP',
			dateFrom: eff[FIELD_KEYS.DROP_DATE_FROM],
			dateUntil: eff[FIELD_KEYS.DROP_DATE_UNTIL],
		},
	];
}
